/*
 * embeeLAB Zierschild-Konfigurator (Shopify, Produkt "Personalisiertes
 * Zierschild - dein Wunschtext"). Eigenstaendige Datei, NICHT die geteilte
 * embeelab-konfigurator.js (Namensschild) - siehe Projekt-Plan
 * memoized-swinging-otter.md.
 *
 * Portiert aus dem Python-Referenztool (D:\embeeLAB_Schildprogramm):
 *   - app/geometry/layout.py   (Textlayout + Auto-Shrink)
 *   - app/geometry/holes.py    (Loch-Automatik)
 *   - app/geometry/preview_svg.py (Buchstaben-Rendering)
 * Die Schildkontur + das Ziernaht-Band werden NICHT neu hergeleitet: sie
 * sind als bereits fertige, statische SVG-Pfad-Strings im JSON enthalten
 * (erzeugt von preview_svg.py selbst, siehe scripts/export_web_data.py),
 * weil diese beiden Konturen nie vom Kundentext abhaengen. Nur die
 * Buchstaben-Positionen und Loch-Positionen sind kundentext-abhaengig und
 * muessen daher hier zur Laufzeit berechnet werden.
 */

// ============================================================
// Daten laden
// ============================================================

const SERVER_DATA = JSON.parse(document.getElementById('el-sc-data').textContent);

// Vorbelegung (Kundenwunsch): Besucher sollen sofort eine gefuellte Vorschau
// sehen statt eines leeren, komplett weissen Schilds. Zeilen + Zierelement
// als "bereits vom Kunden angefasst" behandelt (siehe *Touched unten) - der
// Zierelement-Regler steht dadurch von Anfang an auf 80% statt dem
// technischen Default 100%, exakt wie wenn ein Besucher ihn selbst bewegt
// haette.
const DEFAULT_LINE1 = 'EMBEELAB';
const DEFAULT_LINE2 = 'SCHILDER';
const DEFAULT_ORNAMENT_SCALE = 0.8;

const state = {
  line1: DEFAULT_LINE1,
  line2: DEFAULT_LINE2,
  variantId: SERVER_DATA.variants[0]?.id || null,
  variantTitle: SERVER_DATA.variants[0]?.title || '',
  sides: SERVER_DATA.variants[0]?.sides || 'both',
  price: SERVER_DATA.variants[0]?.price || '',
  environment: 'indoor',
  colorShield: null,
  colorTrim: null,
  hanging: 'holes', // 'holes' | 'keyhole' | 'none' - 'keyhole' nur bei sides='front'
  spacingMm: 110,
  previewSide: 'front',
  // Zierelement "dritte Zeile" (Kundenwunsch) + manuelle Schriftgroessen-/
  // Zierelement-Groessen-Regler. "*Touched" = Nutzer hat den jeweiligen
  // Regler bereits bewegt - solange false, wird kein Override gesendet und
  // der Regler folgt passiv dem automatisch berechneten Wert (siehe
  // updateSizeRanges), analog zum NAS-Referenztool (app/static/app.js).
  ornament: true,
  line1CapMm: null, line1CapTouched: false,
  line2CapMm: null, line2CapTouched: false,
  ornamentScale: DEFAULT_ORNAMENT_SCALE, ornamentScaleTouched: true,
};

let G = null; // Geometrie-JSON (Konstanten, Konturen, Glyphen, statische Pfade)

class ShrinkError extends Error {}

// Die Farb-Dropdowns bleiben leer ("Lade Farben...") bis die ~500KB grosse
// Geometrie-JSON geladen ist - das ist die eigentliche Wartezeit, die Kunden
// als "Farben laden langsam" wahrnehmen. Fortschritt via Content-Length +
// Streaming-Reader, damit ein Prozentwert angezeigt werden kann. Ohne
// Content-Length-Header (z.B. falls ein CDN ihn mal nicht mitschickt) faellt
// die Anzeige auf reinen Text ohne Prozent zurueck statt zu raten.
async function fetchJsonWithProgress(url, onProgress) {
  const response = await fetch(url);
  const totalHeader = response.headers.get('Content-Length');
  const total = totalHeader ? parseInt(totalHeader, 10) : null;

  if (!response.body || !total) {
    onProgress(null);
    return response.json();
  }

  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    // Gzip-komprimierte Antworten liefern mehr entpackte Bytes als
    // Content-Length (Transfergroesse) - deckeln bei 99%, echte 100% erst
    // nach done, damit die Anzeige nie ueberschiesst oder klemmt.
    onProgress(Math.min(99, Math.round((received / total) * 100)));
  }
  onProgress(100);

  const text = await new Blob(chunks).text();
  return JSON.parse(text);
}

function setColorLoadingText(pct) {
  const label = pct === null
    ? 'Farbpalette wird geladen...'
    : `Farbpalette wird geladen... ${pct}%`;
  const nameShield = document.getElementById('el-sc-name-shield');
  const nameTrim = document.getElementById('el-sc-name-trim');
  if (nameShield) nameShield.textContent = label;
  if (nameTrim) nameTrim.textContent = label;
}

function setColorTriggersDisabled(disabled) {
  document.querySelectorAll('.el-sc__color-trigger').forEach(btn => { btn.disabled = disabled; });
}

// Auf einer schnellen Verbindung (CDN + Brotli-Kompression) ist der Fetch oft
// in unter 100ms fertig - der Ladetext blitzt dann nur auf und ist praktisch
// nicht wahrnehmbar (Kundenfeedback: "Ladebalken ist nicht da"). Mindest-
// Anzeigedauer erzwingt, dass der Zustand tatsaechlich sichtbar ist, auch
// wenn die Daten laengst da sind.
const COLOR_LOADING_MIN_MS = 500;

// Farbanzahl je Einsatzbereich (Kundenwunsch: auf einen Blick sehen, wie viele
// Farben in PLA/PETG jeweils zur Auswahl stehen) - steht schon in SERVER_DATA,
// braucht NICHT auf die (viel groessere, asynchron geladene) Geometrie-JSON
// zu warten.
function renderEnvColorCounts() {
  const indoorEl = document.getElementById('el-sc-env-count-indoor');
  const outdoorEl = document.getElementById('el-sc-env-count-outdoor');
  if (indoorEl) indoorEl.textContent = `${SERVER_DATA.colorsPla.length} Farben`;
  if (outdoorEl) outdoorEl.textContent = `${SERVER_DATA.colorsPetg.length} Farben`;
}
renderEnvColorCounts();

async function boot() {
  const t0 = performance.now();
  setColorLoadingText(0);
  setColorTriggersDisabled(true);
  G = await fetchJsonWithProgress(SERVER_DATA.geometryUrl, setColorLoadingText);
  const elapsed = performance.now() - t0;
  if (elapsed < COLOR_LOADING_MIN_MS) {
    await new Promise(r => setTimeout(r, COLOR_LOADING_MIN_MS - elapsed));
  }
  setColorTriggersDisabled(false);
  updateHangingAvailability(); // Aufhaengungs-Default passend zum initial gewaehlten Seiten-Zustand
  initColorDropdowns(); // rendert bereits einmal selbst
  document.getElementById('el-sc-loading')?.classList.add('is-hidden');
}
boot();

// ============================================================
// Layout-Port (aus app/geometry/layout.py)
// ============================================================

function glyphWidthUnits(ch) {
  if (ch === ' ') return G.constants.spaceAdvanceUnits;
  const islands = G.glyphs[ch];
  if (!islands || !islands.length) return 0;
  let xmin = Infinity, xmax = -Infinity;
  for (const poly of islands) {
    for (const [x] of poly.exterior) { if (x < xmin) xmin = x; if (x > xmax) xmax = x; }
  }
  return xmax - xmin;
}

function glyphXminUnits(ch) {
  const islands = G.glyphs[ch];
  if (!islands || !islands.length) return 0;
  let xmin = Infinity;
  for (const poly of islands) for (const [x] of poly.exterior) if (x < xmin) xmin = x;
  return xmin;
}

function fieldWidthAtY(y) {
  const poly = G.contours.trimInnerPoly;
  const rings = [poly.exterior, ...poly.interiors];
  let miny = Infinity, maxy = -Infinity;
  for (const ring of rings) for (const [, yy] of ring) { if (yy < miny) miny = yy; if (yy > maxy) maxy = yy; }
  if (y <= miny || y >= maxy) return 0.0;

  const crossings = [];
  for (const ring of rings) {
    const n = ring.length;
    for (let i = 0; i < n; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[(i + 1) % n];
      if ((y1 <= y && y2 > y) || (y2 <= y && y1 > y)) {
        const t = (y - y1) / (y2 - y1);
        crossings.push(x1 + t * (x2 - x1));
      }
    }
  }
  crossings.sort((a, b) => a - b);
  let best = 0.0;
  for (let i = 0; i + 1 < crossings.length; i += 2) {
    best = Math.max(best, crossings[i + 1] - crossings[i]);
  }
  return best;
}

function layoutLine(text, baseCapMm, baselineY) {
  const entries = [];
  let cursorX = 0.0;
  const gapMm = G.constants.letterGapFraction * baseCapMm;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const capMm = i === 0 ? baseCapMm * G.constants.firstLetterBoost : baseCapMm;
    const scale = capMm / G.constants.fontCapHeightUnits;
    const wUnits = glyphWidthUnits(ch);
    const xminUnits = glyphXminUnits(ch);
    const entryX = cursorX - xminUnits * scale;
    if (ch !== ' ') entries.push({ char: ch, scale, x: entryX, y: baselineY });
    cursorX += wUnits * scale + gapMm;
  }
  const totalWidth = (entries.length > 0 || text.length > 0) ? cursorX - gapMm : 0.0;
  return { entries, totalWidth: Math.max(totalWidth, 0.0) };
}

function recenter(entries, totalWidth) {
  const shift = -totalWidth / 2.0;
  for (const e of entries) e.x += shift;
}

// manualCapMm (Kundenwunsch: Schriftgroessen-Regler pro Zeile): nach der
// Auto-Schrumpf-Berechnung ist capMm bereits exakt die groesste Hoehe, die
// fuer diesen Text noch ins Feld passt ("maxPossibleCapMm"). Ist manualCapMm
// gesetzt, wird ER als finale Hoehe verwendet - aber geklemmt auf
// [minCapMm, maxPossibleCapMm], damit ein Reglerwert nie Buchstaben aus dem
// Feld schneiden oder unter die Mindesthoehe rutschen kann (1:1 Port von
// layout.py layout_line_with_shrink).
function layoutLineWithShrink(text, baseCapMm, minCapMm, baselineY, fieldFn, manualCapMm) {
  if (!text) return { entries: [], capMm: baseCapMm, shrunk: false, maxPossibleCapMm: baseCapMm };

  let capMm = baseCapMm;
  let { entries, totalWidth: width } = layoutLine(text, capMm, baselineY);
  let fieldW = fieldFn(capMm) - 2 * G.constants.fieldMarginMm;
  let shrunk = false;
  if (width > fieldW) {
    shrunk = true;
    capMm = capMm * (fieldW / width);
    if (capMm < minCapMm) {
      throw new ShrinkError(`Text zu lang: "${text}" passt selbst bei Mindesthöhe ${minCapMm}mm nicht in die verfügbare Breite (${fieldW.toFixed(1)}mm).`);
    }
    ({ entries, totalWidth: width } = layoutLine(text, capMm, baselineY));
    for (let i = 0; i < 5; i++) {
      const fieldW2 = fieldFn(capMm) - 2 * G.constants.fieldMarginMm;
      if (width <= fieldW2) break;
      capMm *= fieldW2 / width;
      if (capMm < minCapMm) {
        throw new ShrinkError(`Text zu lang: "${text}" passt selbst bei Mindesthöhe ${minCapMm}mm nicht in die verfügbare Breite.`);
      }
      ({ entries, totalWidth: width } = layoutLine(text, capMm, baselineY));
    }
  }

  const maxPossibleCapMm = capMm;
  if (manualCapMm != null) {
    capMm = Math.max(minCapMm, Math.min(manualCapMm, maxPossibleCapMm));
    ({ entries, totalWidth: width } = layoutLine(text, capMm, baselineY));
  }

  recenter(entries, width);
  return { entries, capMm, shrunk, maxPossibleCapMm };
}

// Zierelement "dritte Zeile" (Kundenwunsch, Port aus app/geometry/ornament.py
// + layout.py). Herz + Zierstriche werden bewusst NICHT als Kontur-Daten
// exportiert (anders als die Buchstaben-Glyphen): beides sind einfache,
// deterministische Formeln (klassische parametrische Herzkurve, Kapsel-
// Striche = Rechteck + Halbkreis-Enden) ohne Winding-Number-/Gap-Merging-
// Sonderfaelle wie bei selbstueberschneidenden Buchstaben - risikoarm direkt
// hier nachgebaut.
function heartLocalPolygon(n) {
  n = n || 72;
  const pts = [];
  for (let i = 0; i < n; i++) {
    const t = (2 * Math.PI * i) / n;
    const x = 16 * Math.pow(Math.sin(t), 3);
    const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
    pts.push([x, y]);
  }
  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (const [x, y] of pts) { if (x < minx) minx = x; if (x > maxx) maxx = x; if (y < miny) miny = y; if (y > maxy) maxy = y; }
  const sx = G.constants.heartWidthMm / (maxx - minx);
  const sy = G.constants.heartHeightMm / (maxy - miny);
  const scaled = pts.map(([x, y]) => [x * sx, y * sy]);
  let minx2 = Infinity, maxx2 = -Infinity, miny2 = Infinity, maxy2 = -Infinity;
  for (const [x, y] of scaled) { if (x < minx2) minx2 = x; if (x > maxx2) maxx2 = x; if (y < miny2) miny2 = y; if (y > maxy2) maxy2 = y; }
  const cx = (minx2 + maxx2) / 2.0, cy = (miny2 + maxy2) / 2.0;
  return scaled.map(([x, y]) => [x - cx, y - cy]);
}

// Kapsel (gerades Mittelstueck von x0 bis x1 + runde Enden, Radius=thickness/2) -
// geometrisch identisch zu shapely LineString([(x0,0),(x1,0)]).buffer(r, cap_style=1)
// (Minkowski-Summe aus Strecke + Kreisscheibe), hier direkt ueber Halbkreis-
// Punkte nachgebaut statt eine Buffer-Bibliothek zu brauchen.
function strokeCapsulePolygon(x0, x1, thickness, segs) {
  segs = segs || 16;
  const r = thickness / 2.0;
  const pts = [];
  for (let i = 0; i <= segs; i++) {
    const a = -Math.PI / 2 + (Math.PI * i) / segs;
    pts.push([x1 + r * Math.cos(a), r * Math.sin(a)]);
  }
  for (let i = 0; i <= segs; i++) {
    const a = Math.PI / 2 + (Math.PI * i) / segs;
    pts.push([x0 + r * Math.cos(a), r * Math.sin(a)]);
  }
  return pts;
}

function buildOrnamentIslands(scale) {
  scale = scale == null ? 1.0 : scale;
  const heart = heartLocalPolygon();
  const halfHeartW = G.constants.heartWidthMm / 2.0;
  const innerX = halfHeartW + G.constants.strokeGapMm;
  const outerX = innerX + G.constants.strokeLenMm;
  const rightStroke = strokeCapsulePolygon(innerX, outerX, G.constants.strokeThicknessMm);
  const leftStroke = rightStroke.map(([x, y]) => [-x, y]);
  const scalePts = (pts) => (scale === 1.0 ? pts : pts.map(([x, y]) => [x * scale, y * scale]));
  return [scalePts(heart), scalePts(leftStroke), scalePts(rightStroke)];
}

function ornamentTotalHeightMm(scale) {
  const islands = buildOrnamentIslands(scale);
  let miny = Infinity, maxy = -Infinity;
  for (const island of islands) for (const [, y] of island) { if (y < miny) miny = y; if (y > maxy) maxy = y; }
  return maxy - miny;
}

function ornamentIslandsWorld(yCenter, mirrorX, scale) {
  const islands = buildOrnamentIslands(scale);
  return islands.map((pts) => {
    let out = pts.map(([x, y]) => [x, y + yCenter]);
    if (mirrorX) out = out.map(([x, y]) => [-x, y]);
    return { exterior: out, interiors: [] };
  });
}

// line1CapMm/line2CapMm/ornamentScale (Kundenwunsch: Regler pro Zeile +
// Zierelement): optionale manuelle Ueberschreibung, siehe layoutLineWithShrink.
// ornament=true: Grundlinie(n) ruecken auf die Zierelement-Positionen.
//
// Kundenkorrektur (Zeilenabstand): Zeile 2s Grundlinie haengt NICHT mehr an
// einer festen Position, sondern mit festem Abstand (lineGapMm) an Zeile 1s
// TATSAECHLICHER Groesse - sonst waechst bei kleinerer Zeile 2 (Schrumpf ODER
// manueller Regler) eine Luecke, weil Buchstaben nur nach oben von ihrer
// Grundlinie wachsen. Aufgeloest per Fixpunkt-Iteration (Baseline<->Cap-
// Zirkularitaet), 1:1 Port aus layout.py build_text_layout. Das Zierelement
// (zweizeilig) haengt analog mit festem Abstand (ornamentGapMm) an Zeile 2s
// (jetzt dynamischer) Grundlinie + der tatsaechlichen (skalierten) Ornament-
// Hoehe. Einzeilig bleibt unveraendert (fixe Grundlinie, fixer Ornament-
// Abstand - dort gibt es das "wachsende Luecke"-Problem nie, siehe layout.py).
function buildTextLayout(line1, line2, ornament, line1CapMm, line2CapMm, ornamentScale) {
  line2 = (line2 || '').trim();
  line1 = (line1 || '').trim();
  const result = { front: [], back: [], autoShrink: false, capHeights: {}, ornament: null, sizeRanges: {} };

  const scaleUsed = ornamentScale == null
    ? G.constants.ornamentScaleMax
    : Math.max(G.constants.ornamentScaleMin, Math.min(ornamentScale, G.constants.ornamentScaleMax));

  if (!line2) {
    const singleBaseline = ornament ? G.constants.singleLineBaselineYOrnament : G.constants.singleLineBaselineY;
    const fieldFn = (capMm) => fieldWidthAtY(singleBaseline + capMm * G.constants.firstLetterBoost);
    const r = layoutLineWithShrink(
      line1, G.constants.singleLineMaxCapMm, G.constants.minCapMm.single, singleBaseline, fieldFn, line1CapMm
    );
    result.front = r.entries;
    result.autoShrink = r.shrunk;
    result.capHeights.line1 = r.capMm;
    result.sizeRanges.line1 = { min: G.constants.minCapMm.single, max: r.maxPossibleCapMm, value: r.capMm };
    if (ornament) {
      result.ornament = { yCenter: G.constants.ornamentYCenterSingle, scale: scaleUsed };
    }
  } else {
    const line1Baseline = ornament ? G.constants.line1BaselineYOrnament : G.constants.line1BaselineY;
    const fieldFn1 = (capMm) => fieldWidthAtY(line1Baseline + capMm * G.constants.firstLetterBoost);
    const r1 = layoutLineWithShrink(line1, G.constants.line1CapMm, G.constants.minCapMm.line1, line1Baseline, fieldFn1, line1CapMm);

    let cap2Guess = line2CapMm == null ? G.constants.line2CapMm : line2CapMm;
    let r2 = null, line2Baseline = null;
    for (let i = 0; i < 6; i++) {
      line2Baseline = line1Baseline - G.constants.lineGapMm - cap2Guess;
      const fieldFn2 = (capMm) => fieldWidthAtY(line2Baseline + capMm * G.constants.firstLetterBoost);
      r2 = layoutLineWithShrink(line2, G.constants.line2CapMm, G.constants.minCapMm.line2, line2Baseline, fieldFn2, line2CapMm);
      if (Math.abs(r2.capMm - cap2Guess) < 0.005) break;
      cap2Guess = r2.capMm;
    }

    result.front = [...r1.entries, ...r2.entries];
    result.autoShrink = r1.shrunk || r2.shrunk;
    result.capHeights.line1 = r1.capMm;
    result.capHeights.line2 = r2.capMm;
    result.sizeRanges.line1 = { min: G.constants.minCapMm.line1, max: r1.maxPossibleCapMm, value: r1.capMm };
    result.sizeRanges.line2 = { min: G.constants.minCapMm.line2, max: r2.maxPossibleCapMm, value: r2.capMm };

    if (ornament) {
      const ornamentTop = line2Baseline - G.constants.ornamentGapMm;
      const yCenter = ornamentTop - ornamentTotalHeightMm(scaleUsed) / 2.0;
      result.ornament = { yCenter, scale: scaleUsed };
    }
  }

  if (ornament) {
    result.sizeRanges.ornament = { min: G.constants.ornamentScaleMin, max: G.constants.ornamentScaleMax, value: scaleUsed };
  }

  result.back = result.front.slice();
  return result;
}

// ============================================================
// Holes-Port (aus app/geometry/holes.py)
// ============================================================

function pointInPolygon(pt, ring) {
  const [px, py] = pt;
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect = ((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointToSegmentDist(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - x1) * dx + (py - y1) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = x1 + t * dx, cy = y1 + t * dy;
  return Math.hypot(px - cx, py - cy);
}

function pointToBoundaryDist(pt, ring) {
  const [px, py] = pt;
  let best = Infinity;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % n];
    best = Math.min(best, pointToSegmentDist(px, py, x1, y1, x2, y2));
  }
  return best;
}

function apexY() {
  let maxY = -Infinity;
  for (const [, y] of G.contours.outerPoly.exterior) if (y > maxY) maxY = y;
  return maxY;
}

function bestHoleY(spacingMm, textTopY) {
  const x = spacingMm / 2.0;
  const yTop = apexY() - 25.0;
  const yBottom = textTopY + G.constants.clearanceToText + G.constants.eyeletRadius;
  const grooveRing = G.contours.grooveInnerPoly.exterior;
  const outerRing = G.contours.outerPoly.exterior;

  for (let y = yTop; y >= yBottom; y -= G.constants.searchStep) {
    const pt = [x, y];
    if (
      pointInPolygon(pt, grooveRing) &&
      pointToBoundaryDist(pt, grooveRing) >= G.constants.eyeletRadius + G.constants.wallToGroove + G.constants.safetyMargin &&
      pointToBoundaryDist(pt, outerRing) >= G.constants.eyeletRadius + G.constants.wallToOuter + G.constants.safetyMargin
    ) {
      return y;
    }
  }
  return null;
}

function eyeletPositions(spacingMm, textTopY) {
  const y = bestHoleY(spacingMm, textTopY);
  if (y === null) return null;
  const x = spacingMm / 2.0;
  return [[-x, y], [x, y]];
}

function validSpacings(textTopY) {
  const out = [];
  for (let s = G.constants.spacingMin; s <= G.constants.spacingMax + 1e-6; s += G.constants.spacingStep) {
    if (bestHoleY(s, textTopY) !== null) out.push(s);
  }
  return out;
}

// Schluesselloch-Aufhaengung (nur Einseitig, siehe app/geometry/holes.py
// best_keyhole_rest_y). Gleiche Wandregeln wie Durchgangsloecher, aber ZWEI
// Punkte muessen sie erfuellen: Rastposition (oben) UND Einfuehrposition
// (keyholeTravel darunter) - das Einfuehrloch ist die breiteste Stelle und
// bestimmt den Wandabstand an beiden Punkten einheitlich.
function bestKeyholeRestY(spacingMm, textTopY) {
  const x = spacingMm / 2.0;
  const r = G.constants.keyholeRadius;
  const yTop = apexY() - 25.0;
  const yBottom = textTopY + G.constants.clearanceToText + r + G.constants.keyholeTravel;
  const grooveRing = G.contours.grooveInnerPoly.exterior;
  const outerRing = G.contours.outerPoly.exterior;

  for (let y = yTop; y >= yBottom; y -= G.constants.searchStep) {
    let ok = true;
    for (const py of [y, y - G.constants.keyholeTravel]) {
      const pt = [x, py];
      if (!(
        pointInPolygon(pt, grooveRing) &&
        pointToBoundaryDist(pt, grooveRing) >= r + G.constants.wallToGroove + G.constants.safetyMargin &&
        pointToBoundaryDist(pt, outerRing) >= r + G.constants.wallToOuter + G.constants.safetyMargin
      )) {
        ok = false;
        break;
      }
    }
    if (ok) return y;
  }
  return null;
}

function keyholePositions(spacingMm, textTopY) {
  const y = bestKeyholeRestY(spacingMm, textTopY);
  if (y === null) return null;
  const x = spacingMm / 2.0;
  return [[-x, y], [x, y]];
}

function validKeyholeSpacings(textTopY) {
  const out = [];
  for (let s = G.constants.spacingMin; s <= G.constants.spacingMax + 1e-6; s += G.constants.spacingStep) {
    if (bestKeyholeRestY(s, textTopY) !== null) out.push(s);
  }
  return out;
}

// ============================================================
// SVG-Rendering (Buchstaben + Löcher; Schildkontur/Ziernaht sind statisch)
// ============================================================

function entryIslandsWorld(entry, mirrorX) {
  const islands = G.glyphs[entry.char];
  const s = entry.scale;
  function tf(coords) {
    return coords.map(([x, y]) => {
      let wx = x * s + entry.x;
      const wy = y * s + entry.y;
      if (mirrorX) wx = -wx;
      return [wx, wy];
    });
  }
  return islands.map(poly => ({ exterior: tf(poly.exterior), interiors: poly.interiors.map(tf) }));
}

function ringToPolylineD(coords, fx) {
  let d = `M ${(fx * coords[0][0]).toFixed(2)},${(-coords[0][1]).toFixed(2)} `;
  for (let i = 1; i < coords.length; i++) {
    const [x, y] = coords[i];
    d += `L ${(fx * x).toFixed(2)},${(-y).toFixed(2)} `;
  }
  return d + 'Z';
}

function letterPolyToPathD(poly, flipX) {
  const fx = flipX ? -1 : 1;
  let d = ringToPolylineD(poly.exterior, fx);
  for (const interior of poly.interiors) d += ' ' + ringToPolylineD(interior, fx);
  return d;
}

// Layout + Loch-Suche haengen NUR von Text/Loch-Einstellungen ab, NICHT von
// Farbe/Seite/Vorschau-Ansicht. bestHoleY() ist eine lineare 0.5mm-Schrittsuche
// gegen ~1500-Punkt-Konturen (Punkt-in-Polygon + 2x Rand-Abstand pro Schritt) -
// bei jeder Farbauswahl neu zu rechnen macht die UI spuerbar traege (Kunden-
// feedback: Innen/Aussen-Wechsel "dauert super lange"). Cache invalidiert sich
// selbst, sobald sich Text oder Loch-Konfiguration aendern.
let _layoutHolesCache = { key: null, textLayout: null, eyelets: null, keyholes: null, error: null };

function computeLayoutAndHoles(line1, line2, hanging, spacingMm, ornament, line1CapMm, line2CapMm, ornamentScale) {
  const key = `${line1} ${line2} ${hanging} ${spacingMm} ${ornament} ${line1CapMm} ${line2CapMm} ${ornamentScale}`;
  if (_layoutHolesCache.key === key) return _layoutHolesCache;

  let textLayout;
  try {
    textLayout = buildTextLayout(line1, line2, ornament, line1CapMm, line2CapMm, ornamentScale);
  } catch (e) {
    if (e instanceof ShrinkError) {
      const result = { key, textLayout: null, eyelets: null, keyholes: null, error: e.message };
      _layoutHolesCache = result;
      return result;
    }
    throw e;
  }

  const cap1 = textLayout.capHeights.line1;
  const textTopY = line2.trim()
    ? (ornament ? G.constants.line1BaselineYOrnament : G.constants.line1BaselineY) + cap1 * G.constants.firstLetterBoost
    : (ornament ? G.constants.singleLineBaselineYOrnament : G.constants.singleLineBaselineY) + cap1 * G.constants.firstLetterBoost;

  let eyelets = null;
  let keyholes = null;
  if (hanging === 'holes') {
    eyelets = eyeletPositions(spacingMm, textTopY);
    if (eyelets === null) {
      const result = {
        key, textLayout, eyelets: null, keyholes: null,
        error: `Abstand ${spacingMm}mm ist bei dieser Textkonfiguration nicht sicher möglich.`,
      };
      _layoutHolesCache = result;
      return result;
    }
  } else if (hanging === 'keyhole') {
    keyholes = keyholePositions(spacingMm, textTopY);
    if (keyholes === null) {
      const result = {
        key, textLayout, eyelets: null, keyholes: null,
        error: `Abstand ${spacingMm}mm ist für die Wandmontage bei dieser Textkonfiguration nicht sicher möglich.`,
      };
      _layoutHolesCache = result;
      return result;
    }
  }

  const result = { key, textLayout, eyelets, keyholes, error: null };
  _layoutHolesCache = result;
  return result;
}

// Bemaßungslinien (Gesamtbreite/-höhe des Schilds, Kundenwunsch: Maße auf
// einen Blick sehen, nicht nur einen 50mm-Referenzbalken).
function dimLineH(xWorldStart, xWorldEnd, yWorld, label) {
  const svgY = -yWorld;
  const tick = 3;
  return `<g stroke="#666" stroke-width="0.5" font-size="7" font-family="sans-serif" fill="#666">` +
    `<line x1="${xWorldStart.toFixed(1)}" y1="${(svgY - tick).toFixed(1)}" x2="${xWorldStart.toFixed(1)}" y2="${(svgY + tick).toFixed(1)}"/>` +
    `<line x1="${xWorldEnd.toFixed(1)}" y1="${(svgY - tick).toFixed(1)}" x2="${xWorldEnd.toFixed(1)}" y2="${(svgY + tick).toFixed(1)}"/>` +
    `<line x1="${xWorldStart.toFixed(1)}" y1="${svgY.toFixed(1)}" x2="${xWorldEnd.toFixed(1)}" y2="${svgY.toFixed(1)}"/>` +
    `<text x="${((xWorldStart + xWorldEnd) / 2).toFixed(1)}" y="${(svgY + 10).toFixed(1)}" text-anchor="middle">${label}</text>` +
    `</g>`;
}

function dimLineV(xWorld, yWorldStart, yWorldEnd, label) {
  const svgX = xWorld;
  const svgY1 = -yWorldStart, svgY2 = -yWorldEnd;
  const tick = 3;
  const midY = (svgY1 + svgY2) / 2;
  const textX = (svgX - 6).toFixed(1);
  return `<g stroke="#666" stroke-width="0.5" font-size="7" font-family="sans-serif" fill="#666">` +
    `<line x1="${(svgX - tick).toFixed(1)}" y1="${svgY1.toFixed(1)}" x2="${(svgX + tick).toFixed(1)}" y2="${svgY1.toFixed(1)}"/>` +
    `<line x1="${(svgX - tick).toFixed(1)}" y1="${svgY2.toFixed(1)}" x2="${(svgX + tick).toFixed(1)}" y2="${svgY2.toFixed(1)}"/>` +
    `<line x1="${svgX.toFixed(1)}" y1="${svgY1.toFixed(1)}" x2="${svgX.toFixed(1)}" y2="${svgY2.toFixed(1)}"/>` +
    `<text x="${textX}" y="${midY.toFixed(1)}" text-anchor="middle" transform="rotate(-90 ${textX} ${midY.toFixed(1)})">${label}</text>` +
    `</g>`;
}

function buildSvg(config) {
  const { line1, line2, sides, hanging, spacingMm, colorShield, colorTrim, side, ornament, line1CapMm, line2CapMm, ornamentScale } = config;

  const cached = computeLayoutAndHoles(line1, line2, hanging, spacingMm, ornament, line1CapMm, line2CapMm, ornamentScale);
  if (cached.error) return { error: cached.error };
  const { textLayout, eyelets, keyholes } = cached;

  const isBack = side === 'back';
  const showTrimAndLetters = (sides === 'both') || !isBack;
  const mirror = isBack;
  const flip = isBack;

  const outerRing = G.contours.outerPoly.exterior;
  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (const [x, y] of outerRing) { if (x < minx) minx = x; if (x > maxx) maxx = x; if (y < miny) miny = y; if (y > maxy) maxy = y; }
  const pad = 30;
  const vbX = minx - pad, vbY = -(maxy) - pad;
  const vbW = (maxx - minx) + 2 * pad, vbH = (maxy - miny) + 2 * pad;

  const parts = [];
  parts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbX.toFixed(1)} ${vbY.toFixed(1)} ${vbW.toFixed(1)} ${vbH.toFixed(1)}">`);
  parts.push('<defs><filter id="el-sc-softshadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="1.5" stdDeviation="2" flood-opacity="0.35"/></filter></defs>');

  const outerPath = isBack ? G.staticPaths.outerPathBack : G.staticPaths.outerPathFront;
  parts.push(`<path d="${outerPath}" fill="${colorShield}" filter="url(#el-sc-softshadow)" stroke="#00000022" stroke-width="0.5"/>`);

  if (showTrimAndLetters) {
    const trimPaths = isBack ? G.staticPaths.trimBandPathBack : G.staticPaths.trimBandPathFront;
    for (const d of trimPaths) parts.push(`<path d="${d}" fill="${colorTrim}"/>`);

    for (const entry of textLayout.front) {
      for (const poly of entryIslandsWorld(entry, mirror)) {
        parts.push(`<path d="${letterPolyToPathD(poly, flip)}" fill="${colorTrim}"/>`);
      }
    }

    // Zierelement (Herz + Zierstriche) - gleiche Farbe/Pipeline wie die
    // Buchstaben (Herz+Striche sind bilateral symmetrisch, daher ist die
    // Rueckseiten-Spiegelung optisch ein No-Op, siehe ornamentIslandsWorld).
    if (textLayout.ornament) {
      const { yCenter, scale } = textLayout.ornament;
      for (const poly of ornamentIslandsWorld(yCenter, mirror, scale)) {
        parts.push(`<path d="${letterPolyToPathD(poly, flip)}" fill="${colorTrim}"/>`);
      }
    }
  }

  if (eyelets) {
    const fx = flip ? -1 : 1;
    for (const [ex, ey] of eyelets) {
      parts.push(`<circle cx="${(fx * ex).toFixed(2)}" cy="${(-ey).toFixed(2)}" r="${G.constants.eyeletRadius.toFixed(2)}" fill="#ffffff" fill-opacity="0.85" stroke="#00000033" stroke-width="0.3"/>`);
    }
  }

  // Schlüssellöcher: rückseitiger Blindschnitt, daher NUR auf der Rückansicht
  // zu sehen (von vorne ist die Schale komplett geschlossen). Kapsel-
  // Silhouette (Einführkreis + Verbindungskanal bis zur Rastposition)
  // entspricht dem tatsächlichen Schnitt-Umriss der Kopftasche.
  if (keyholes && isBack) {
    const fx = flip ? -1 : 1;
    const r = G.constants.keyholeRadius;
    for (const [kx, kyRest] of keyholes) {
      const kyEntry = kyRest - G.constants.keyholeTravel;
      const cx = fx * kx;
      const cyEntry = -kyEntry, cyRest = -kyRest;
      parts.push(`<circle cx="${cx.toFixed(2)}" cy="${cyEntry.toFixed(2)}" r="${r.toFixed(2)}" fill="#ffffff" fill-opacity="0.6" stroke="#00000033" stroke-width="0.3"/>`);
      const rectY = Math.min(cyEntry, cyRest);
      const rectH = Math.abs(cyRest - cyEntry);
      parts.push(`<rect x="${(cx - r).toFixed(2)}" y="${rectY.toFixed(2)}" width="${(2 * r).toFixed(2)}" height="${rectH.toFixed(2)}" fill="#ffffff" fill-opacity="0.6" stroke="#00000033" stroke-width="0.3"/>`);
    }
  }

  // Gesamtbreite (horizontal, unter dem Schild) + Gesamthöhe (vertikal, links
  // davon) - feste Schildaußenmaße, unabhängig vom Kundentext.
  const widthMm = Math.round(maxx - minx);
  const heightMm = Math.round(maxy - miny);
  parts.push(dimLineH(minx, maxx, miny - 10, `${widthMm}mm`));
  parts.push(dimLineV(minx - 10, miny, maxy, `${heightMm}mm`));

  const spacingPoints = eyelets || keyholes;
  if (spacingPoints) {
    const [[x1, y1], [x2, y2]] = spacingPoints;
    const yy = -(Math.min(y1, y2) - 10);
    parts.push(
      `<g stroke="#a33" stroke-width="0.5" font-size="6" font-family="sans-serif" fill="#a33">` +
      `<line x1="${x1.toFixed(1)}" y1="${yy.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${yy.toFixed(1)}"/>` +
      `<text x="${((x1 + x2) / 2 - 10).toFixed(1)}" y="${(yy - 3).toFixed(1)}">${(x2 - x1).toFixed(0)}mm</text></g>`
    );
  }

  parts.push('</svg>');
  return { svg: parts.join('\n'), autoShrink: textLayout.autoShrink, sizeRanges: textLayout.sizeRanges };
}

// ============================================================
// Colour dropdowns (Muster aus embeelab-konfigurator.js)
// ============================================================

function activeColors() {
  return state.environment === 'indoor' ? SERVER_DATA.colorsPla : SERVER_DATA.colorsPetg;
}

// WICHTIG: renderColorDropdown() wird bei JEDEM Innen/Aussen-Wechsel erneut
// aufgerufen, um die Eintraege neu aufzubauen (andere Farbliste). Der Trigger-
// Button-Klick-Handler darf deshalb NICHT hier drin registriert werden - sonst
// stapeln sich bei jedem Aufruf weitere Listener auf demselben Button (er wird
// ja nicht neu erzeugt), und zwei gegenlaeufige Listener oeffnen das Dropdown
// und schliessen es im selben Klick sofort wieder (realer Bug, siehe
// initColorTriggerToggle - die läuft dafür separat nur EINMAL pro Button).
function renderColorDropdown(rootEl, target) {
  const menu = rootEl.querySelector('.el-sc__color-menu');
  menu.innerHTML = '';
  for (const c of activeColors()) {
    const entry = document.createElement('button');
    entry.type = 'button';
    entry.className = 'el-sc__color-entry';

    const sw = document.createElement('span');
    sw.className = 'el-sc__swatch';
    sw.style.background = c.hex;
    entry.appendChild(sw);

    const name = document.createElement('span');
    name.textContent = c.name;
    entry.appendChild(name);

    entry.addEventListener('click', (ev) => {
      ev.stopPropagation();
      selectColor(target, c);
      closeAllDropdowns();
      renderAll();
    });
    menu.appendChild(entry);
  }
}

function initColorTriggerToggle(rootEl) {
  const trigger = rootEl.querySelector('.el-sc__color-trigger');
  const menu = rootEl.querySelector('.el-sc__color-menu');
  trigger.addEventListener('click', (ev) => {
    ev.stopPropagation();
    const wasOpen = !menu.hidden;
    closeAllDropdowns();
    if (!wasOpen) {
      menu.hidden = false;
      rootEl.classList.add('is-open');
      const active = menu.querySelector('.is-active');
      if (active) active.scrollIntoView({ block: 'nearest' });
    }
  });
}

function closeAllDropdowns() {
  for (const root of document.querySelectorAll('.el-sc__color')) {
    root.classList.remove('is-open');
    const m = root.querySelector('.el-sc__color-menu');
    if (m) m.hidden = true;
  }
  const errorTooltip = document.getElementById('el-sc-error-tooltip');
  if (errorTooltip) errorTooltip.hidden = true;
}
document.addEventListener('click', closeAllDropdowns);

// Info-Tooltip an der Fehlermeldung (Kundenwunsch: kurz erklaeren warum ein
// Lochabstand/die Wandmontage gerade nicht moeglich ist). Klick statt Hover
// (funktioniert auch auf dem Handy), Text wird bereits in renderAll() passend
// zur aktuellen Aufhaengungs-Art gesetzt (siehe ERROR_EXPLANATIONS).
document.getElementById('el-sc-error-info')?.addEventListener('click', (ev) => {
  ev.stopPropagation();
  const tooltip = document.getElementById('el-sc-error-tooltip');
  if (!tooltip) return;
  const wasHidden = tooltip.hidden;
  closeAllDropdowns();
  tooltip.hidden = !wasHidden;
});

function selectColor(target, color) {
  if (target === 'shield') state.colorShield = color;
  else state.colorTrim = color;
  const swId = target === 'shield' ? 'el-sc-swatch-shield' : 'el-sc-swatch-trim';
  const nameId = target === 'shield' ? 'el-sc-name-shield' : 'el-sc-name-trim';
  document.getElementById(swId).style.background = color.hex;
  document.getElementById(nameId).textContent = color.name;

  const rootEl = document.querySelector(`.el-sc__color[data-target="${target}"]`);
  const menu = rootEl.querySelector('.el-sc__color-menu');
  for (const e of menu.children) e.classList.remove('is-active');
  for (const e of menu.children) {
    const label = e.querySelector('span:not(.el-sc__swatch)');
    if (label && label.textContent === color.name) { e.classList.add('is-active'); break; }
  }
  // KEIN renderAll() hier - wird bewusst von den Aufrufern gebündelt (siehe
  // initColorDropdowns/onEnvironmentChanged), damit ein Umschalten, das BEIDE
  // Farben betrifft, nicht zwei volle SVG-Neuberechnungen hintereinander
  // auslöst (spürbarer Ruckler beim Innen/Außen-Wechsel, Kundenfeedback).
}

// Vorbelegte Standardfarben (Kundenwunsch): Schild schwarz, Text/Deko gelb
// (innen) bzw. weiss (aussen). "Gelb" trifft zufaellig exakt die embeeLAB-
// Markenakzentfarbe (#f6c400). "Gold" als Ausweichoption fuer Innen, falls
// "Gelb" in einer Palette mal fehlen sollte (Kundenwunsch: "gelb oder gold,
// was da ist"). Aussen/PETG hat aktuell nur Weiss+Schwarz zur Auswahl - ohne
// diese Praeferenz wuerde der reine Index-Fallback (frueher: 1. Farbe =
// Schild, 2. Farbe = Text) fuer BEIDE auf "Schwarz" fallen, sobald die
// vorherige Textfarbe (z.B. Gelb) beim Wechsel auf PETG nicht existiert -
// Schild und Schrift waeren dann identisch schwarz und die Schrift
// "verschwindet" optisch (genau der gemeldete Bug).
const DEFAULT_SHIELD_COLOR_NAME = 'Schwarz';
const DEFAULT_TRIM_COLOR_NAMES = { indoor: ['Gelb', 'Gold'], outdoor: ['Weiss'] };

function pickPreferredColor(colors, preferredNames, fallbackIndex) {
  for (const name of preferredNames) {
    const found = colors.find((c) => c.name === name);
    if (found) return found;
  }
  return colors[fallbackIndex] ?? colors[0];
}

function preferredTrimColor(colors, fallbackIndex) {
  return pickPreferredColor(colors, DEFAULT_TRIM_COLOR_NAMES[state.environment] || [], fallbackIndex);
}

function initColorDropdowns() {
  const shieldRoot = document.querySelector('.el-sc__color[data-target="shield"]');
  const trimRoot = document.querySelector('.el-sc__color[data-target="trim"]');
  renderColorDropdown(shieldRoot, 'shield');
  renderColorDropdown(trimRoot, 'trim');
  initColorTriggerToggle(shieldRoot);
  initColorTriggerToggle(trimRoot);
  const colors = activeColors();
  if (colors.length) {
    selectColor('shield', pickPreferredColor(colors, [DEFAULT_SHIELD_COLOR_NAME], 0));
    selectColor('trim', preferredTrimColor(colors, Math.min(1, colors.length - 1)));
  }
  renderAll();
}

function onEnvironmentChanged() {
  renderColorDropdown(document.querySelector('.el-sc__color[data-target="shield"]'), 'shield');
  renderColorDropdown(document.querySelector('.el-sc__color[data-target="trim"]'), 'trim');
  const colors = activeColors();
  const stillValidShield = colors.find(c => c.name === state.colorShield?.name);
  const stillValidTrim = colors.find(c => c.name === state.colorTrim?.name);
  selectColor('shield', stillValidShield || pickPreferredColor(colors, [DEFAULT_SHIELD_COLOR_NAME], 0));
  selectColor('trim', stillValidTrim || preferredTrimColor(colors, Math.min(1, colors.length - 1)));
  renderAll();
}

// ============================================================
// UI wiring
// ============================================================

const line1Input = document.getElementById('el-sc-line1');
const line2Input = document.getElementById('el-sc-line2');
const line1Hint = document.getElementById('el-sc-line1-hint');
const line2Hint = document.getElementById('el-sc-line2-hint');
const CHARSET_PATTERN = /[^A-Z0-9ÄÖÜß ]/g;

function sanitizeLine(value) {
  return value.toUpperCase().replace(CHARSET_PATTERN, '').slice(0, 20);
}

// War ein Zeichen WIRKLICH nicht erlaubt (nicht nur klein geschrieben)? Muss
// beide Seiten grossschreiben, bevor verglichen wird - sonst meldet jeder
// Kleinbuchstabe faelschlich einen Fehler, weil sanitizeLine() zusaetzlich
// grossschreibt und cleaned dadurch IMMER vom rohen (kleingeschriebenen)
// input.value abweicht (realer Bug, Kundenfeedback: Fehlermeldung bei ganz
// normalen Buchstaben).
function hasDisallowedChars(value) {
  const upper = value.toUpperCase();
  return upper.replace(CHARSET_PATTERN, '') !== upper;
}

function wireLineInput(input, hintEl, key) {
  input?.addEventListener('input', () => {
    if (hasDisallowedChars(input.value)) {
      flashHintError(hintEl, input, 'Erlaubt sind nur Buchstaben (A-Z, ä ö ü ß), Zahlen und Leerzeichen.');
    }
    const cleaned = sanitizeLine(input.value);
    input.value = cleaned;
    state[key] = cleaned;
    renderAll();
  });
}
wireLineInput(line1Input, line1Hint, 'line1');
wireLineInput(line2Input, line2Hint, 'line2');

function flashHintError(hintEl, inputEl, msg) {
  if (!hintEl) return;
  const original = hintEl.dataset.original || hintEl.textContent;
  hintEl.dataset.original = original;
  hintEl.textContent = msg;
  hintEl.classList.add('is-error');
  inputEl.classList.add('has-error');
  clearTimeout(flashHintError._t);
  flashHintError._t = setTimeout(() => {
    hintEl.textContent = original;
    hintEl.classList.remove('is-error');
    inputEl.classList.remove('has-error');
  }, 2200);
}

document.querySelectorAll('.el-sc__variant').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('.el-sc__variant').forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    state.variantId = parseInt(btn.dataset.variantId, 10);
    state.variantTitle = btn.dataset.title;
    state.price = btn.dataset.price;
    state.sides = btn.dataset.sides;
    if (state.sides === 'front') { state.previewSide = 'front'; }
    updateHangingAvailability();
    renderAll();
  });
});

// Wandmontage (Schluesselloch) ist nur bei Einseitig moeglich (schneidet von
// der Rueckseite einer standalone-Vorderschale, siehe cad_export.
// build_keyhole_cutter). Bei Beidseitig wird die Dropdown-Option gesperrt +
// auf Loecher zurueckgefallen. Default bei Einseitig ist bewusst "keine
// Aufhaengung" (Nutzer-Entscheid).
function updateHangingAvailability() {
  const keyholeOption = hangingSelect?.querySelector('option[value="keyhole"]');
  if (!keyholeOption) return;
  if (state.sides === 'both') {
    keyholeOption.disabled = true;
    if (state.hanging === 'keyhole' || state.hanging === 'none') setHanging('holes');
  } else {
    keyholeOption.disabled = false;
    setHanging('none');
  }
}

function setHanging(val) {
  state.hanging = val;
  if (hangingSelect) hangingSelect.value = val;
  if (spacingRow) spacingRow.style.display = val !== 'none' ? '' : 'none';
}

document.querySelectorAll('.el-sc__env').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('.el-sc__env').forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    state.environment = btn.dataset.env;
    onEnvironmentChanged();
  });
});

document.querySelectorAll('.el-sc__side').forEach(btn => {
  btn.addEventListener('click', () => {
    if (btn.disabled) return;
    document.querySelectorAll('.el-sc__side').forEach(b => b.classList.remove('is-active'));
    btn.classList.add('is-active');
    state.previewSide = btn.dataset.side;
    renderAll();
  });
});

const spacingSlider = document.getElementById('el-sc-spacing');
const spacingValue = document.getElementById('el-sc-spacing-value');
const spacingRow = document.getElementById('el-sc-spacing-row');
const hangingSelect = document.getElementById('el-sc-hanging');

hangingSelect?.addEventListener('change', () => {
  setHanging(hangingSelect.value);
  renderAll();
});

spacingSlider?.addEventListener('input', () => {
  state.spacingMm = parseFloat(spacingSlider.value);
  if (spacingValue) spacingValue.textContent = `${state.spacingMm}mm`;
  renderAll();
});

// ============================================================
// Zierelement + manuelle Schriftgroessen-/Zierelement-Groessen-Regler
// (Kundenwunsch, Port aus app/static/app.js im NAS-Referenztool)
// ============================================================

const ornamentCheckbox = document.getElementById('el-sc-ornament');
const ornamentSizeRow = document.getElementById('el-sc-ornament-size-row');
const ornamentSizeSlider = document.getElementById('el-sc-ornament-size');
const ornamentSizeValue = document.getElementById('el-sc-ornament-size-value');
const line1SizeRow = document.getElementById('el-sc-line1-size-row');
const line1SizeSlider = document.getElementById('el-sc-line1-size');
const line1SizeValue = document.getElementById('el-sc-line1-size-value');
const line2SizeRow = document.getElementById('el-sc-line2-size-row');
const line2SizeSlider = document.getElementById('el-sc-line2-size');
const line2SizeValue = document.getElementById('el-sc-line2-size-value');

ornamentCheckbox?.addEventListener('change', (e) => {
  state.ornament = e.target.checked;
  if (ornamentSizeRow) ornamentSizeRow.style.display = state.ornament ? '' : 'none';
  renderAll();
});

line1SizeSlider?.addEventListener('input', () => {
  state.line1CapTouched = true;
  state.line1CapMm = parseFloat(line1SizeSlider.value);
  if (line1SizeValue) line1SizeValue.textContent = state.line1CapMm.toFixed(1);
  renderAll();
});
line2SizeSlider?.addEventListener('input', () => {
  state.line2CapTouched = true;
  state.line2CapMm = parseFloat(line2SizeSlider.value);
  if (line2SizeValue) line2SizeValue.textContent = state.line2CapMm.toFixed(1);
  renderAll();
});
ornamentSizeSlider?.addEventListener('input', () => {
  state.ornamentScaleTouched = true;
  state.ornamentScale = parseFloat(ornamentSizeSlider.value);
  if (ornamentSizeValue) ornamentSizeValue.textContent = String(Math.round(state.ornamentScale * 100));
  renderAll();
});

// Regler-Grenzen haengen vom aktuellen Text/Zierelement-Status ab und werden
// bei jedem Render neu gesetzt (Kundenwunsch: Regler darf nur Werte zulassen,
// die fuer den aktuellen Text tatsaechlich passen) - 1:1 Port aus
// app/static/app.js updateSizeRange/updateSizeRanges.
function updateSizeRange(slider, valueEl, range, touchedKey, valueKey, displayFn) {
  if (!slider || !range) return;
  slider.min = range.min;
  slider.max = range.max;
  if (!state[touchedKey]) {
    state[valueKey] = range.value;
  } else if (state[valueKey] < range.min) {
    state[valueKey] = range.min;
  } else if (state[valueKey] > range.max) {
    state[valueKey] = range.max;
  }
  slider.value = state[valueKey];
  if (valueEl) valueEl.textContent = displayFn ? displayFn(state[valueKey]) : state[valueKey].toFixed(1);
}

function updateSizeRanges(sizeRanges) {
  if (!sizeRanges) return;
  updateSizeRange(line1SizeSlider, line1SizeValue, sizeRanges.line1, 'line1CapTouched', 'line1CapMm');
  if (sizeRanges.line2) {
    if (line2SizeRow) line2SizeRow.style.display = '';
    updateSizeRange(line2SizeSlider, line2SizeValue, sizeRanges.line2, 'line2CapTouched', 'line2CapMm');
  } else if (line2SizeRow) {
    line2SizeRow.style.display = 'none';
  }
  if (sizeRanges.ornament) {
    updateSizeRange(ornamentSizeSlider, ornamentSizeValue, sizeRanges.ornament, 'ornamentScaleTouched', 'ornamentScale', (v) => String(Math.round(v * 100)));
  }
}

// ============================================================
// Dicken-Profil (Seitenansicht, Kundenwunsch: Ring/Buchstaben "rausstehen"
// sehen). Schematische Darstellung, nicht die echte Kontur - hängt nur von
// Seiten-Auswahl + Farben ab, daher immer billig neu zu zeichnen.
// ============================================================

function profileDimV(x, y1, y2, label) {
  const tick = 2;
  const mid = (y1 + y2) / 2;
  return `<g stroke="#888" stroke-width="0.4" font-size="6" font-family="sans-serif" fill="#888">` +
    `<line x1="${(x - tick).toFixed(1)}" y1="${y1.toFixed(1)}" x2="${(x + tick).toFixed(1)}" y2="${y1.toFixed(1)}"/>` +
    `<line x1="${(x - tick).toFixed(1)}" y1="${y2.toFixed(1)}" x2="${(x + tick).toFixed(1)}" y2="${y2.toFixed(1)}"/>` +
    `<line x1="${x.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x.toFixed(1)}" y2="${y2.toFixed(1)}"/>` +
    `<text x="${(x + 4).toFixed(1)}" y="${(mid + 2).toFixed(1)}">${label}</text>` +
    `</g>`;
}

// Echte Ueberstaende (1.5mm Ring, 4mm Buchstaben) sind bei 10mm Schalendicke
// bereits ein deutlich sichtbarer Anteil (15%/40%) - anders als beim ersten
// (falschen) Buchstaben-Wert von nur 1mm, der eine kuenstliche Vergroesserung
// noetig machte. Leichter Boost (1.6x) nur fuer Kanten-Schaerfe bei kleiner
// Darstellung, keine verzerrende Uebertreibung mehr. Legende + Gesamtdicke-
// Massband zeigen ohnehin immer die echten mm-Werte, unabhaengig vom Boost.
const PROFILE_BUMP_VISUAL_SCALE = 1.6;

// Der Zierring laeuft RINGS UM die gesamte Kontur - ein Querschnitt durch
// die Mitte des Schilds schneidet ihn deshalb an BEIDEN Raendern (links UND
// rechts), nicht nur einmal (Kundenfeedback: "der Zierring fehlt ja rechts").
// Die Buchstaben liegen dazwischen auf der Flaeche - hier nur 3 stellver-
// tretende Markierungen, NICHT die tatsaechlich eingegebenen Buchstaben
// (waere bei einer Schnittansicht ohnehin nicht sinnvoll darstellbar).
function buildProfileSvg() {
  const both = state.sides === 'both';
  const frontH = G.constants.shellFrontMm;
  const backH = G.constants.shellBackMm;
  // Ring: TRIM_HEIGHT ist die Gesamthoehe des Ring-Bauteils, GROOVE_DEPTH wie
  // tief es in der Schale versenkt sitzt -> Ueberstand = Differenz.
  const ringBump = (G.constants.trimHeightMm - G.constants.grooveDepthMm) * PROFILE_BUMP_VISUAL_SCALE;
  // Buchstaben: LETTER_RELIEF ist (siehe common.py-Kommentar "Verhaeltnis
  // eingesteckt/sichtbar") bereits DIREKT der sichtbare Ueberstand, NICHT die
  // Gesamthoehe - das Buchstabenteil ist insgesamt RELIEF+POCKET_DEPTH=7mm
  // hoch, wovon POCKET_DEPTH (3mm) in der Tasche sitzt und RELIEF (4mm)
  // rausragt (verifiziert gegen cad_export.py build_letters: z0_letter =
  // SHELL_FRONT - LETTER_POCKET_DEPTH, Buchstabe reicht bis z0_letter+7,
  // also 4mm ueber die Schild-Oberflaeche hinaus - keine Subtraktion noetig).
  const letterBump = G.constants.letterReliefMm * PROFILE_BUMP_VISUAL_SCALE;
  // Schmalerer Streifen (statt 100) + grosszuegigere Basis-Polsterung, damit
  // das Bild nicht als duenner Balken wirkt, sondern bei "width:100%,
  // height:auto" auch auf schmalen Mobile-Containern noch ordentlich hoch
  // gerendert wird (Kundenfeedback: "Fenster zu kurz").
  const stripLen = 75;
  const shieldColor = state.colorShield?.hex || G.constants.fallbackColorShield;
  const trimColor = state.colorTrim?.hex || G.constants.fallbackColorTrim;

  const maxBump = Math.max(ringBump, letterBump);
  const labelRowH = 9; // Platz fuer "Ring"/"Buchstaben"-Beschriftung ueber den Bumps
  const padTop = maxBump + labelRowH + 18;
  const padBottom = both ? maxBump + 22 : 16;
  const realTotalH = frontH + (both ? backH : 0);
  const svgH = realTotalH + padTop + padBottom;
  const x0 = 20;
  const svgW = x0 + stripLen + 44; // genug Rand rechts, sonst wird die mm-Beschriftung abgeschnitten

  const bump = (x, yBase, w, h, up) =>
    `<rect x="${x.toFixed(1)}" y="${(up ? yBase - h : yBase).toFixed(1)}" width="${w}" height="${h.toFixed(2)}" ` +
    `fill="${trimColor}" stroke="#0004" stroke-width="0.2"/>`;

  const surfaceLine = (y) =>
    `<line x1="${x0}" y1="${y.toFixed(1)}" x2="${(x0 + stripLen).toFixed(1)}" y2="${y.toFixed(1)}" ` +
    `stroke="#999" stroke-width="0.3" stroke-dasharray="1.5,1.2"/>`;

  const label = (x, y, text) =>
    `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" font-size="6" font-family="sans-serif" fill="#999" text-anchor="middle">${text}</text>`;

  // Ring-Bumps an BEIDEN Raendern des Streifens (Perimeter wird zweimal
  // geschnitten), Buchstaben-Bumps mittig dazwischen.
  const ringW = 9;
  const letterW = 6;
  const letterGap = 11;
  const midX = x0 + stripLen / 2;
  const letterXs = [midX - letterGap - letterW / 2, midX - letterW / 2, midX + letterGap - letterW / 2];

  const parts = [];
  parts.push(`<svg viewBox="0 0 ${svgW.toFixed(1)} ${svgH.toFixed(1)}" xmlns="http://www.w3.org/2000/svg">`);

  const frontY0 = padTop;
  parts.push(`<rect x="${x0}" y="${frontY0.toFixed(1)}" width="${stripLen}" height="${frontH.toFixed(1)}" fill="${shieldColor}" stroke="#0006" stroke-width="0.3"/>`);
  parts.push(surfaceLine(frontY0));
  parts.push(bump(x0, frontY0, ringW, ringBump, true));
  parts.push(bump(x0 + stripLen - ringW, frontY0, ringW, ringBump, true));
  for (const lx of letterXs) parts.push(bump(lx, frontY0, letterW, letterBump, true));
  parts.push(label(x0 + ringW / 2, frontY0 - maxBump - 3, 'Ring'));
  parts.push(label(midX, frontY0 - maxBump - 3, 'Buchstaben'));

  let totalBottom = frontY0 + frontH;
  if (both) {
    const backY0 = totalBottom;
    parts.push(`<rect x="${x0}" y="${backY0.toFixed(1)}" width="${stripLen}" height="${backH.toFixed(1)}" fill="${shieldColor}" stroke="#0006" stroke-width="0.3"/>`);
    totalBottom = backY0 + backH;
    parts.push(surfaceLine(totalBottom));
    parts.push(bump(x0, totalBottom, ringW, ringBump, false));
    parts.push(bump(x0 + stripLen - ringW, totalBottom, ringW, ringBump, false));
    for (const lx of letterXs) parts.push(bump(lx, totalBottom, letterW, letterBump, false));
  }

  const dimX = x0 + stripLen + 14;
  parts.push(profileDimV(dimX, frontY0, totalBottom, `${realTotalH.toFixed(0)}mm`));

  parts.push('</svg>');
  return parts.join('');
}

function renderProfile() {
  const profileEl = document.getElementById('el-sc-profile');
  const legendEl = document.getElementById('el-sc-profile-legend');
  if (profileEl) profileEl.innerHTML = buildProfileSvg();
  if (legendEl) {
    const ringBump = (G.constants.trimHeightMm - G.constants.grooveDepthMm).toFixed(1);
    const letterBump = G.constants.letterReliefMm.toFixed(1);
    const trimColor = state.colorTrim?.hex || G.constants.fallbackColorTrim;
    let html =
      `<span class="el-sc__profile-legend-item"><span class="el-sc__profile-legend-swatch" style="background:${trimColor}"></span>Zierring: +${ringBump}mm</span>` +
      `<span class="el-sc__profile-legend-item"><span class="el-sc__profile-legend-swatch" style="background:${trimColor}"></span>Buchstaben: +${letterBump}mm</span>`;
    if (state.hanging === 'holes') {
      const holeDiameter = (G.constants.eyeletRadius * 2).toFixed(0);
      html += `<span class="el-sc__profile-legend-item"><span class="el-sc__profile-legend-swatch" style="background:#999"></span>Loch-Ø: ${holeDiameter}mm</span>`;
    } else if (state.hanging === 'keyhole') {
      html += `<span class="el-sc__profile-legend-item"><span class="el-sc__profile-legend-swatch" style="background:#999"></span>Wandmontage für Schraubenkopf bis Ø9mm</span>`;
    }
    legendEl.innerHTML = html;
  }
}

// Kurze Erklaerungen fuer den Info-Tooltip an der Fehlermeldung (Kunden-
// wunsch): warum ist ein Lochabstand/die Wandmontage bei dieser Konfiguration
// nicht sicher moeglich? Je nach Aufhaengungs-Art ein anderer Grund - bei
// der Wandmontage geht es um die tragende Restwand (Presssitz-Boden der
// Buchstaben-Tasche wuerde sonst mit weggefraest), nicht nur um Optik.
const ERROR_EXPLANATIONS = {
  holes: 'Das Loch braucht Sicherheitsabstand zur Ziernaht, zur Außenkante und zu den Buchstaben (die Bohrung würde sonst mittendurch gehen). Bei größerer Schrift oder engem Lochabstand bleibt oben nicht genug Platz dafür - kleinere Schrift oder ein anderer Abstand schaffen Abhilfe.',
  keyhole: 'Die Wandmontage wird von der Rückseite eingefräst und braucht dafür oberhalb der Buchstaben eine tragfähige Restwand - überschneidet sie sich mit einer Buchstaben-Tasche, bliebe an der Stelle, wo später das ganze Schild-Gewicht hängt, kaum noch Material stehen. Bei größerer Schrift bleibt oben nicht mehr genug Platz - kleinere Schrift oder ein anderer Abstand schaffen Abhilfe.',
};

function renderAll() {
  const viewer = document.getElementById('el-sc-viewer');
  const errorEl = document.getElementById('el-sc-error');
  const errorTextEl = document.getElementById('el-sc-error-text');
  const errorTooltipEl = document.getElementById('el-sc-error-tooltip');
  // Seiten-Vorschau (Vorderseite/Rückseite) ist auch bei sides='front'
  // relevant, wenn Schlüsselloch gewählt ist - die Silhouetten sind ja nur
  // rückseitig sichtbar (siehe buildSvg).
  const showSideToggle = state.sides === 'both' || state.hanging === 'keyhole';
  const config = {
    line1: state.line1,
    line2: state.line2,
    sides: state.sides,
    hanging: state.hanging,
    spacingMm: state.spacingMm,
    colorShield: state.colorShield?.hex || G.constants.fallbackColorShield,
    colorTrim: state.colorTrim?.hex || G.constants.fallbackColorTrim,
    side: showSideToggle ? state.previewSide : 'front',
    ornament: state.ornament,
    line1CapMm: state.line1CapTouched ? state.line1CapMm : null,
    line2CapMm: state.line2CapTouched ? state.line2CapMm : null,
    ornamentScale: state.ornamentScaleTouched ? state.ornamentScale : null,
  };
  const result = buildSvg(config);
  if (result.error) {
    if (errorTextEl) errorTextEl.textContent = result.error;
    if (errorTooltipEl) {
      errorTooltipEl.textContent = ERROR_EXPLANATIONS[state.hanging] || '';
      errorTooltipEl.hidden = true; // bei neuer/geaenderter Fehlermeldung erst mal zuklappen
    }
    if (errorEl) errorEl.hidden = false;
  } else {
    if (errorEl) errorEl.hidden = true;
    if (viewer) viewer.innerHTML = result.svg;
    updateSizeRanges(result.sizeRanges);
  }

  const sideToggle = document.getElementById('el-sc-side-toggle');
  if (sideToggle) sideToggle.style.display = showSideToggle ? '' : 'none';

  document.getElementById('el-sc-sum-sides').textContent = state.variantTitle || '-';
  document.getElementById('el-sc-sum-thickness').textContent = state.sides === 'both' ? '2cm' : '1cm';
  document.getElementById('el-sc-sum-env').textContent = state.environment === 'indoor' ? 'Innen (PLA)' : 'Außen (PETG)';
  let hangingText = 'Keine';
  if (state.hanging === 'holes') hangingText = `Löcher, ${state.spacingMm}mm`;
  else if (state.hanging === 'keyhole') hangingText = `Wandmontage, ${state.spacingMm}mm`;
  document.getElementById('el-sc-sum-hanging').textContent = hangingText;
  document.getElementById('el-sc-sum-price').textContent = state.price || '';
  renderProfile();

  const addBtn = document.getElementById('el-sc-add');
  const hasText = state.line1.trim().length > 0;
  if (addBtn) addBtn.disabled = !(hasText && state.colorShield && state.colorTrim && state.variantId && !result.error);
}

// ============================================================
// Add to cart
// ============================================================

const addBtn = document.getElementById('el-sc-add');
const successEl = document.getElementById('el-sc-success');

addBtn?.addEventListener('click', () => {
  if (addBtn.disabled) return;
  const props = {
    'Zeile 1': state.line1,
    'Zeile 2': state.line2,
    'Seiten': state.variantTitle,
    'Dicke': state.sides === 'both' ? '2cm' : '1cm',
    'Einsatzbereich': state.environment === 'indoor' ? 'Innen (PLA)' : 'Außen (PETG)',
    'Farbe Schild': state.colorShield ? `${state.colorShield.name} (${state.colorShield.hex})` : '',
    'Farbe Text & Deko': state.colorTrim ? `${state.colorTrim.name} (${state.colorTrim.hex})` : '',
    'Aufhängung': state.hanging === 'holes' ? `Löcher zum Aufhängen, ${state.spacingMm}mm Abstand`
      : state.hanging === 'keyhole' ? `Wandmontage (unsichtbar), ${state.spacingMm}mm Abstand`
      : 'Keine',
    'Zierelement': state.ornament ? 'Ja (Herz + Zierstriche)' : 'Nein',
  };
  // Manuell veraenderte Schriftgroessen-/Zierelement-Groessen-Regler nur
  // dann als eigene Property mitschicken, wenn der Kunde sie tatsaechlich
  // angefasst hat - damit Matthias die exakte Konfiguration spaeter 1:1 im
  // NAS-Tool nachbauen kann (sonst greift dort ohnehin dieselbe Auto-Groesse).
  if (state.line1CapTouched) props['Zeile 1 Größe'] = `${state.line1CapMm.toFixed(1)}mm (manuell)`;
  if (state.line2CapTouched) props['Zeile 2 Größe'] = `${state.line2CapMm.toFixed(1)}mm (manuell)`;
  if (state.ornament && state.ornamentScaleTouched) props['Zierelement-Größe'] = `${Math.round(state.ornamentScale * 100)}% (manuell)`;
  const orig = addBtn.textContent;
  addBtn.disabled = true;
  addBtn.textContent = '...';
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: state.variantId, quantity: 1, properties: props }),
  })
    .then(r => r.json())
    .then(item => {
      if (item.id) {
        successEl?.classList.add('is-shown');
        setTimeout(() => { window.location.href = '/cart'; }, 1200);
      } else {
        addBtn.disabled = false;
        addBtn.textContent = orig;
        alert('Fehler: ' + (item.description || JSON.stringify(item)));
      }
    })
    .catch(err => {
      addBtn.disabled = false;
      addBtn.textContent = orig;
      alert('Netzwerkfehler: ' + err.message);
    });
});
